import { useState } from 'react';
import WebinarHtml from './WebinarHtml';
import { Container } from './WebinarShared';
import { GiftIcon } from './WebinarIcons';

const FIELD_CLASS =
  'w-full rounded-lg border border-[#e5e5e5] px-3.5 py-3 text-[15px] text-grey-950 focus:border-transparent focus:outline-2 focus:outline-offset-1 focus:outline-brand-400';

const WebinarSignup = ({
  bonusTitle,
  bonusHeading,
  bonusText,
  formTitle,
  formSubtitle,
  firstNameLabel,
  firstNamePlaceholder,
  lastNameLabel,
  lastNamePlaceholder,
  emailLabel,
  emailPlaceholder,
  companyLabel,
  companyPlaceholder,
  consentText,
  submitLabel,
  subscribeLists,
  tagName,
  successMessage,
  successNote,
  errorMessage,
  loadingLabel
}) => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', company: '' });
  const [honeypot, setHoneypot] = useState('');
  const [formState, setFormState] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState('loading');

    try {
      const lists = (subscribeLists || 'kurz_datoverizenyemailing')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      // Leadhub identify() only accepts email/first_name/last_name/subscribe/user_id/phone
      // (see leadhub.co/integracni-manual). Custom attributes are NOT supported here.
      const identifyData = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        subscribe: lists
      };

      // Custom properties (e.g. company) go through the tag's `data` object.
      const company = formData.company.trim();
      const tagData = {
        name: 'webinar',
        data: {
          id: tagName || 'cely_kurz',
          ...(company ? { company } : {}),
          date: { datatype: 'datetime', value: new Date().toISOString() }
        }
      };

      fetch('/api/webinar-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identify: identifyData, tag: tagData, hp: honeypot })
      });

      if (typeof window !== 'undefined' && window.lhi) {
        window.lhi('identify', identifyData);
        window.lhi('addTag', tagData);
      }

      setFormState('success');
    } catch {
      setFormState('error');
    }
  };

  return (
    <section
      id="prihlaska"
      className="bg-white bg-[radial-gradient(600px_380px_at_15%_0%,#fff3ed_0%,rgba(255,243,237,0)_70%)] py-16 md:py-[88px]"
    >
      <Container>
        {(bonusTitle || bonusHeading || bonusText) && (
          <div className="mx-auto mb-10 max-w-[560px] text-center">
            <div className="bg-brand-400 mx-auto mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-full text-white">
              <GiftIcon width={26} height={26} />
            </div>
            {bonusTitle && <div className="font-poly text-brand-500 mb-3 text-[32px] font-light">{bonusTitle}</div>}
            {bonusHeading && (
              <h4 className="font-poly text-grey-950 mb-3 text-2xl font-light tracking-[0.8px]">{bonusHeading}</h4>
            )}
            {bonusText && <p className="text-grey-500 text-base leading-[1.6]">{bonusText}</p>}
          </div>
        )}

        <div className="mx-auto max-w-[560px] rounded-2xl border border-[#e5e5e5] bg-white p-10 shadow-[0_24px_48px_-24px_rgba(29,24,22,0.14)]">
          {formTitle && <h2 className="font-poly text-grey-950 mb-2.5 text-[28px] font-light">{formTitle}</h2>}
          {formSubtitle && <p className="text-grey-500 mb-7 text-[15px]">{formSubtitle}</p>}

          {formState === 'success' ? (
            <div className="rounded-lg bg-green-50 p-6 text-center text-green-700">
              <p className="font-medium">{successMessage || 'Hotovo, jste přihlášeni!'}</p>
              {successNote && <p className="mt-2 text-sm">{successNote}</p>}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                data-lpignore="true"
                data-1p-ignore
                data-form-type="other"
                className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0"
              />
              <div className="mb-4">
                <label className="text-grey-950 mb-1.5 block text-[13px] font-medium">
                  {firstNameLabel || 'Jméno'}
                </label>
                <input
                  type="text"
                  required
                  autoComplete="given-name"
                  placeholder={firstNamePlaceholder}
                  value={formData.firstName}
                  onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                  className={FIELD_CLASS}
                />
              </div>
              <div className="mb-4">
                <label className="text-grey-950 mb-1.5 block text-[13px] font-medium">
                  {lastNameLabel || 'Příjmení'}
                </label>
                <input
                  type="text"
                  required
                  autoComplete="family-name"
                  placeholder={lastNamePlaceholder}
                  value={formData.lastName}
                  onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                  className={FIELD_CLASS}
                />
              </div>
              <div className="mb-4">
                <label className="text-grey-950 mb-1.5 block text-[13px] font-medium">{emailLabel || 'E-mail'}</label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  placeholder={emailPlaceholder}
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                  className={FIELD_CLASS}
                />
              </div>
              <div className="mb-4">
                <label className="text-grey-950 mb-1.5 block text-[13px] font-medium">{companyLabel || 'Firma'}</label>
                <input
                  type="text"
                  autoComplete="organization"
                  placeholder={companyPlaceholder}
                  value={formData.company}
                  onChange={(e) => setFormData((p) => ({ ...p, company: e.target.value }))}
                  className={FIELD_CLASS}
                />
              </div>

              {consentText && (
                <div className="my-5 flex items-start gap-2.5">
                  <input type="checkbox" required className="mt-[3px] h-4 w-4 shrink-0" />
                  <WebinarHtml html={consentText} className="text-grey-500 text-[13px] leading-[1.5] [&_a]:underline" />
                </div>
              )}

              {formState === 'error' && (
                <div className="text-brand-600 mb-3 text-sm">
                  {errorMessage || 'Něco se pokazilo. Zkuste to znovu.'}
                </div>
              )}

              <button
                type="submit"
                disabled={formState === 'loading'}
                className="bg-brand-400 hover:bg-brand-500 ml-auto flex items-center justify-center rounded-full px-7 py-[15px] text-base text-white transition-colors disabled:opacity-60"
              >
                {formState === 'loading' ? loadingLabel || 'Odesílám…' : submitLabel || 'Zaregistrovat se zdarma'}
              </button>
            </form>
          )}
        </div>
      </Container>
    </section>
  );
};

export default WebinarSignup;
